import React, {Component} from 'react';
import {toast} from 'react-toastify';
import {withTranslation} from 'react-i18next';
import {APIClient} from "../../../utils/APIClient";
import {URLQueryStringParser} from "../../../utils/URLQueryStringParser";
import {API_URL} from '../../../settings/local_settings';
import {Loading} from "../../Loading";


import {withRouterParams} from "../../../utils/withRouterParams";
import { TIKTOK_CHANNEL } from '../../channels/channels';
import {clearOauthReturn, destinationOf, errorDestinationOf, peekOauthReturn} from '../../onboarding/oauthReturn';
import {connectedDestinationFor} from './connectedDestination';
import {consumeReconnect} from '../connections/reconnectNote';
import {
    announceReconnectNotice, originOf, reconnectNotice, reconnectResultOf, recordReconnectResult,
} from '../connections/reconnectAccountText';

// Códigos tipados que devuelve GetTiktokAdsAccountCredentials. Cualquier otro cae al mensaje genérico:
// el `error_message` del backend no se muestra nunca. Lo único del backend que llega a pantalla es
// `existing_label`, acotado a la agencia (para un cliente del Portal el conflicto sale como
// CONNECTION_NOT_AVAILABLE, sin label). Mismo contrato que `FacebookLink`.
const KNOWN_ERROR_CODES = ['TIKTOK_USER_ALREADY_CONNECTED', 'TIKTOK_TOKEN_EXCHANGE_FAILED'];

export function tiktokErrorMessage(t, data) {
    const code = data && (data.error_code || data.status);
    if (!KNOWN_ERROR_CODES.includes(code)) return t('tiktok_link.error.generic');
    const label = existingLabelFrom(data);
    if (code === 'TIKTOK_USER_ALREADY_CONNECTED' && !label) {
        return t('tiktok_link.error.TIKTOK_USER_ALREADY_CONNECTED_unnamed');
    }
    return t('tiktok_link.error.' + code, {label, interpolation: {escapeValue: false}});
}

function existingLabelFrom(data) {
    // `errors` viaja como string JSON (ErrorResponse hace json.dumps).
    try {
        const extra = typeof data.errors === 'string' ? JSON.parse(data.errors) : (data.errors || {});
        return extra.existing_label || '';
    } catch (e) {
        return '';
    }
}

/** El destino al volver: con el acceso que quedó (en una reconexión, el que ya existía), para desplegarlo. */
export function connectedDestination(data) {
    return connectedDestinationFor(TIKTOK_CHANNEL, data);
}

class ConnectTiktok extends Component {
    constructor(props) {
        super(props);
        this.componentDidMount = this.componentDidMount.bind(this);
    }

    componentDidMount() {
        // La nota se lee UNA vez, al montar, y el destino viaja capturado: leerla al final
        // —dentro del `.then()` del POST— la encontraba borrada cuando el marco del túnel ya se
        // había montado, y devolvía al usuario a la pantalla clásica encima del recorrido. Ver
        // `destinationOf` en `oauthReturn`.
        const tunnelReturn = peekOauthReturn();
        const inTunnel = Boolean(tunnelReturn);

        // Navegar y CONSUMIR la nota: es de un solo uso y acá el recorrido termina. Si sobrevive,
        // el «atrás» del navegador vuelve sin `code` y pinta la tarjeta como fallida sobre una
        // conexión que anduvo, y hasta quince minutos después secuestra al túnel la próxima
        // conexión hecha desde la pantalla clásica. Ver `ConnectAdwordsAccount`.
        const irA = destino => {
            this.props.history.push(destino);
            clearOauthReturn();
        };
        let code = new URLQueryStringParser().getUrlParams('code');
        let auth_code = new URLQueryStringParser().getUrlParams('auth_code');
        let credential_label = localStorage.getItem('pending_credential_label');
        localStorage.removeItem('pending_credential_label');
        // Qué se estaba reconectando: se consume acá, salga bien o mal, y sólo si es de TikTok
        // (`reconnectNote`). La reasignación de la cuenta la hace el backend.
        const reconnect = consumeReconnect(TIKTOK_CHANNEL);
        const origin = originOf(reconnect);
        let postData = {'code': code, 'auth_code': auth_code, 'credential_label': credential_label};
        if (reconnect && reconnect.adAccountId) postData['reconnect_ad_account_id'] = reconnect.adAccountId;
        if (reconnect && reconnect.credentialId) postData['reconnect_credential_id'] = reconnect.credentialId;
        const failed = (data) => {
            // Un mensaje propio, nunca el del backend: puede nombrar a otra agencia («esa cuenta ya
            // está conectada a…») y eso no puede salir a pantalla.
            if (!inTunnel) toast.error(tiktokErrorMessage(this.props.t, data), {autoClose: false});
            irA(errorDestinationOf(tunnelReturn, '/agency/conections?just_connected=' + TIKTOK_CHANNEL,
                                                       TIKTOK_CHANNEL));
        };

        new APIClient(API_URL + 'get_access_tokens/tiktok/').call('POST', postData).then((data) => {
            if (data && data.status === 'OK') {
                const result = reconnectResultOf(data);
                recordReconnectResult(TIKTOK_CHANNEL, result, origin);
                irA(destinationOf(tunnelReturn, connectedDestination(data)));
                if (!inTunnel) {
                    const notice = reconnectNotice(this.props.t, TIKTOK_CHANNEL, result, origin);
                    if (notice) announceReconnectNotice(notice);
                    else toast.success(this.props.t('tiktok_link.connected'));
                }
                return;
            }
            // Sin rama de salida, un corte de red dejaba «Por favor espere…» para siempre: APIClient
            // no rechaza, así que el error llega como una respuesta más y antes no entraba en ningún
            // camino.
            failed(data);
        }).catch(() => failed(null));
    }

    render() {
        return <Loading message={this.props.t('connections.oauth.connecting')}/>
    }
}

export default withTranslation()(withRouterParams(ConnectTiktok));
